(async function () {
  // ─── CONFIG ────────────────────────────────────────────────────────────────
  const OFFER_VARIANT_ID    = 40630425321658; // ← Replace with your variant ID
  const DISCOUNT_PERCENT    = 50;             // ← Discount percentage (50 = 50% off)

  const MIN_PAID_ITEMS      = 2;              // ← Minimum paid items to trigger offer (0 = ignore)
  const MIN_PAID_SPEND_CENTS = 0;             // ← Minimum paid spend in cents to trigger (0 = ignore)

  const SESSION_KEY         = 'upsell_offer_dismissed'; // sessionStorage key
  const OFFER_PROP_KEY      = 'upsell_offer';           // line item property to identify offer line
  const OFFER_PROP_VAL      = 'true';
  const BANNER_ID           = 'upsell-offer-banner';
  const DEBOUNCE_MS         = 300;
  // ───────────────────────────────────────────────────────────────────────────

  let syncing       = false;
  let debounceTimer = null;
  let lastCartHash  = null;

  // ─── Cart helpers ──────────────────────────────────────────────────────────

  function getCartHash(cart) {
    return cart.items
      .map(i => `${i.id}:${i.quantity}:${i.properties?.[OFFER_PROP_KEY] || ''}`)
      .sort()
      .join('|');
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  function isOfferLine(item) {
    return item.id === OFFER_VARIANT_ID && item.properties?.[OFFER_PROP_KEY] === OFFER_PROP_VAL;
  }

  function getOfferLines(cart) {
    return cart.items.filter(isOfferLine);
  }

  function countPaidItems(cart) {
    return cart.items.reduce((sum, it) => sum + (isOfferLine(it) ? 0 : it.quantity), 0);
  }

  function calcPaidSpend(cart) {
    return cart.items.reduce((sum, it) => isOfferLine(it) ? sum : sum + it.final_line_price, 0);
  }

  function qualifiesForOffer(cart) {
    const items = countPaidItems(cart);
    const spend = calcPaidSpend(cart);
    const itemsOk = MIN_PAID_ITEMS  > 0 ? items >= MIN_PAID_ITEMS  : true;
    const spendOk = MIN_PAID_SPEND_CENTS > 0 ? spend >= MIN_PAID_SPEND_CENTS : true;
    return itemsOk && spendOk;
  }

  // ─── Discount price helper ─────────────────────────────────────────────────

  async function getVariantPrice() {
    try {
      const res  = await fetch(`/variants/${OFFER_VARIANT_ID}.js`, { cache: 'no-store' });
      if (!res.ok) return null;
      const data = await res.json();
      return data.price; // in cents
    } catch {
      return null;
    }
  }

  // ─── Cart mutations ────────────────────────────────────────────────────────

  async function addOfferItem(discountedPriceCents) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: OFFER_VARIANT_ID,
          quantity: 1,
          properties: {
            [OFFER_PROP_KEY]: OFFER_PROP_VAL,
            _discounted_price: discountedPriceCents, // informational; actual discount via Shopify Scripts/Functions
          },
        }],
      }),
    });
    if (!res.ok) throw new Error('Failed to add offer item');
    return true;
  }

  async function removeOfferLines(cart) {
    const lines = getOfferLines(cart);
    if (!lines.length) return false;

    const updates = {};
    for (const l of lines) updates[l.key] = 0;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
    return true;
  }

  // ─── Session dismissal ─────────────────────────────────────────────────────

  function isDismissed()  { return sessionStorage.getItem(SESSION_KEY) === '1'; }
  function setDismissed() { sessionStorage.setItem(SESSION_KEY, '1'); }

  // ─── Banner UI ─────────────────────────────────────────────────────────────

  function findCartContainer() {
    // Tries common cart drawer/page selectors — adjust to your theme
    return (
      document.querySelector('[data-cart-form]')       ||
      document.querySelector('.cart__items')            ||
      document.querySelector('.cart-items')             ||
      document.querySelector('[id*="CartItems"]')       ||
      document.querySelector('[class*="cart-drawer"]')  ||
      document.querySelector('form[action="/cart"]')
    );
  }

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    // ↑ Change locale/currency to match your store
  }

  function injectBanner(originalCents, discountedCents) {
    if (document.getElementById(BANNER_ID)) return; // already present

    const container = findCartContainer();
    if (!container) {
      console.warn('🎁 Upsell: cart container not found — adjust findCartContainer()');
      return;
    }

    const saving = originalCents - discountedCents;

    const banner = document.createElement('div');
    banner.id = BANNER_ID;
    banner.style.cssText = `
      background: #fff8f0;
      border: 1.5px solid #f4a84a;
      border-radius: 8px;
      padding: 14px 16px;
      margin: 12px 0;
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: inherit;
      font-size: 14px;
      line-height: 1.4;
    `;

    banner.innerHTML = `
      <div style="flex:1">
        <strong style="display:block;margin-bottom:4px">🎁 Oferta exclusiva para você!</strong>
        <span style="color:#555">
          Adicione este produto por apenas
          <strong style="color:#c0392b">${formatMoney(discountedCents)}</strong>
          <s style="color:#999;font-size:12px">${formatMoney(originalCents)}</s>
          — economia de ${formatMoney(saving)}!
        </span>
      </div>
      <div style="display:flex;flex-direction:column;gap:6px;flex-shrink:0">
        <button id="${BANNER_ID}-accept" style="
          background:#f4a84a;color:#fff;border:none;border-radius:6px;
          padding:8px 14px;cursor:pointer;font-size:13px;font-weight:600;
          white-space:nowrap;
        ">Adicionar</button>
        <button id="${BANNER_ID}-decline" style="
          background:none;color:#999;border:none;cursor:pointer;
          font-size:12px;text-decoration:underline;padding:0;
        ">Não, obrigado</button>
      </div>
    `;

    container.insertAdjacentElement('beforebegin', banner);

    document.getElementById(`${BANNER_ID}-accept`).addEventListener('click', async () => {
      const btn = document.getElementById(`${BANNER_ID}-accept`);
      btn.disabled = true;
      btn.textContent = '...';
      try {
        await addOfferItem(discountedCents);
        removeBanner();
        lastCartHash = null;
        notifyCartUpdate();
        console.log('✅ Upsell: offer item added');
      } catch (err) {
        console.error('❌ Upsell: failed to add offer item', err);
        btn.disabled = false;
        btn.textContent = 'Adicionar';
      }
    });

    document.getElementById(`${BANNER_ID}-decline`).addEventListener('click', () => {
      setDismissed();
      removeBanner();
      console.log('🚫 Upsell: dismissed for session');
    });

    console.log('🎁 Upsell banner injected');
  }

  function removeBanner() {
    document.getElementById(BANNER_ID)?.remove();
  }

  // ─── Cart update notification ──────────────────────────────────────────────

  function notifyCartUpdate() {
    // Fires the same event bonus.js listens to, so CartJS + section reload happen
    $(document).trigger('cart.requestComplete');
  }

  // ─── Main sync ─────────────────────────────────────────────────────────────

  async function syncOfferNow() {
    if (syncing) {
      console.log('⏳ Upsell: already syncing, skipping');
      return;
    }

    syncing = true;
    console.log('🔄 Upsell: sync started');

    try {
      const cart        = await fetchCart();
      const currentHash = getCartHash(cart);

      if (currentHash === lastCartHash) {
        console.log('✅ Upsell: cart unchanged, skipping');
        return;
      }

      lastCartHash = currentHash;

      const qualifies   = qualifiesForOffer(cart);
      const offerLines  = getOfferLines(cart);
      const hasOffer    = offerLines.length > 0;
      const dismissed   = isDismissed();

      console.log(`📊 Upsell — qualifies:${qualifies} hasOffer:${hasOffer} dismissed:${dismissed}`);

      if (!qualifies) {
        // Cart dropped below minimum — clean up offer line and banner silently
        if (hasOffer) {
          await removeOfferLines(cart);
          lastCartHash = null;
          notifyCartUpdate();
        }
        removeBanner();
        return;
      }

      if (hasOffer) {
        // Offer already in cart — make sure banner is gone
        removeBanner();
        return;
      }

      if (dismissed) {
        // Customer said no — respect their choice
        removeBanner();
        return;
      }

      // ── Detect if customer manually removed the offer line ──
      // If cart qualifies, offer not present, and hash changed since last sync,
      // the customer may have just removed it. Flag as dismissed.
      // (We only reach here if dismissed === false, so first-time or post-refresh.)
      // To avoid showing the banner again after manual removal, we track whether
      // we previously had an offer line and it's now gone.
      const hadOfferBefore = sessionStorage.getItem('upsell_had_offer') === '1';

      if (hadOfferBefore && !hasOffer) {
        // Customer removed it — treat as dismissed
        setDismissed();
        sessionStorage.removeItem('upsell_had_offer');
        removeBanner();
        console.log('🚫 Upsell: offer removed by customer — dismissed');
        return;
      }

      // ── Show the banner ──
      const originalCents    = await getVariantPrice();
      if (!originalCents) {
        console.warn('⚠️ Upsell: could not fetch variant price');
        return;
      }

    } catch (err) {
      console.error('❌ Upsell: sync error', err);
      lastCartHash = null;
    } finally {
      // Track whether offer line is in cart for next cycle's removal detection
      try {
        const cart = await fetchCart();
        const has  = getOfferLines(cart).length > 0;
        if (has) sessionStorage.setItem('upsell_had_offer', '1');
        else     sessionStorage.removeItem('upsell_had_offer');
      } catch { /* silent */ }

      syncing = false;
      console.log('✅ Upsell: sync done');
    }
  }

  function syncOfferDebounced() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncOfferNow, DEBOUNCE_MS);
  }

  // ─── Bind ──────────────────────────────────────────────────────────────────

  $(document).off('cart.requestComplete', syncOfferDebounced);
  $(document).on('cart.requestComplete', syncOfferDebounced);

  setTimeout(syncOfferNow, 400); // slightly after bonus.js (300ms) to avoid race
})();
